import React, { Fragment, useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Dialog, Transition } from "@headlessui/react";
import moment from "moment";
import { numberWithCommas } from "../helpers";
import { Beat } from "../../plugins/spinners.plugin";
//images
import CLOSE from "../../assets/images/icons/close.svg";
import { ReactComponent as SHIPPERS } from "../../assets/images/icons/Delivery.svg";
import Warning from "../../assets/images/icons/warning-circle.svg";
import { ChevronDownIcon } from "@heroicons/react/outline";
//Api
import { filterPublicShippers } from "../../redux/publicShippers/publicShippersActions";
import { storeCurrentShipper } from "../../redux/publicShippers/publicShippersActions";
import { isAuthenticatedUser } from "../../utils/Auth";
import {
  storePublicCartProducts,
  showMyCart,
} from "../../redux/cart/cartAction";

export function SelectShipperModal(props) {
  const { setCurrentShipper, user, currentAddress, currentShipper } = props;
  const dispatch = useDispatch();
  const isLoggedIn = isAuthenticatedUser();
  const [selected, setSelected] = useState({});
  const { filterPublicShippersData } = useSelector(
    (state) => state.publicShippers
  );
  const { storing } = useSelector((state) => state.cart);
  const { settings, isLoading } = useSelector((state) => state.application);


  useEffect(() => {
    // Fetch shippers for both logged-in and guest users
    dispatch(
      filterPublicShippers({
        date: moment(new Date()).format("YYYY-MM-DD HH:mm"),
        pagination: 0,
      })
    );
    // eslint-disable-next-line
  }, []);

  // Set initial selected shipper when modal opens
  useEffect(() => {
    if (currentShipper?.id) {
      setSelected(currentShipper);
    }
  }, [currentShipper, props.show]);

  const handleSubmit = async () => {
    // Validate delivery mode selection when required
    if (settings?.delivery_mode_checkout_status?.toLowerCase() === "available" &&
      selected?.delivery_modes?.length > 0 &&
      !selected?.delivery_mode_id) {
      alert("Please select a delivery mode");
      return;
    }

    setCurrentShipper(selected);
    dispatch(storeCurrentShipper(selected));

    // Only call authenticated APIs if user is logged in
    if (isLoggedIn && user?.id) {
      try {
        const cartPayload = {
          user_id: user?.id,
          shipper_id: selected?.id
        };

        // Add delivery_mode_id if it exists
        if (selected?.delivery_mode_id) {
          cartPayload.delivery_mode_id = selected?.delivery_mode_id;
        }

        const response = await dispatch(storePublicCartProducts(cartPayload));

        if (response?.status_code === 200) {
          const showCartPayload = {
            shipper_id: selected?.id,
          };

          // Add delivery_mode_id if it exists
          if (selected?.delivery_mode_id) {
            showCartPayload.delivery_mode_id = selected?.delivery_mode_id;
          }

          if (currentAddress?.length > 0) {
            showCartPayload.address_id = currentAddress?.[0]?.id;
          }

          dispatch(showMyCart(showCartPayload));
        }
      } catch (error) {
        console.log("Error updating cart with shipper:", error);
      }
    }

    props.onClose();
  };

  return (
    <div data-test="addressModal">
      <Transition show={props.show} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={props.onClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed z-10 inset-0 overflow-y-auto">
            <div className="flex items-center sm:items-center justify-center min-h-full p-4 sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative w-[650px] bg-white dark:bg-[#121212] rounded-3xl px-4 pt-5 pb-4 max-h-[90vh] h-full overflow-scroll shadow-xl transform transition-all sm:my-10 sm:max-w-[650px] sm:w-full  sm:p-8">
                  <div className="sm:block absolute top-1 sm:top-3 right-4 pt-4 pr-4">
                    <button
                      type="button"
                      className="bg-[#ffeae5] rounded-3xl p-2 sm:p-3 outline-none"
                      onClick={props.onClose}
                    >
                      <span className="sr-only">Close</span>
                      <img
                        src={CLOSE}
                        alt="close"
                        className=" w-3 h-3 sm:w-4 sm:h-4"
                      />
                    </button>
                  </div>
                  <div className="sm:flex">
                    <div className="text-left sm:mt-2">
                      <Dialog.Title
                        as="h3"
                        className="text-base font-medium text-black dark:text-white"
                      >
                        Select Shipper <span className="hidden sm:inline-block">(Choose a delivery partner)</span>
                      </Dialog.Title>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-4">
                    <div className="mt-4 border-b-2 "></div>

                    {/* Warning note */}
                    <div className="mt-8 px-2 sm:px-5 py-2 sm:py-3 flex gap-3 items-start justify-center bg-[#FEF7F8] dark:bg-[#010101] rounded-lg">

                      <div className="px-2 py-2 rounded-full bg-[#FEE9E9] flex-none">
                        <img
                          src={Warning}
                          alt="warning"
                          className="w-[18px] h-[18px]"
                        />
                      </div>

                      <div>
                        <p className="text-sm text-[#333333] dark:text-[#BCBCBC] mb-2">
                          Shippers are independent delivery partners.
                        </p>
                        <p className="text-sm text-[#333333] dark:text-[#BCBCBC]">
                          Their delivery fees and estimated delivery times may differ, so choose the option that works best for you.
                        </p>
                      </div>

                    </div>

                    <div className="mt-8">
                      {isLoading ?
                        <p>Loading...</p>
                        :
                        <>
                          {settings?.delivery_mode_checkout_status?.toLowerCase() === "available" ?
                            <ul>
                              {/* If delivery_mode_checkout_status === available */}
                              {filterPublicShippersData?.length > 0 ? (
                                filterPublicShippersData?.map((shipper) => (
                                  <ShipperDeliveryModes
                                    key={shipper?.id}
                                    selected={selected}
                                    setSelected={setSelected}
                                    shipper={shipper}
                                  />
                                ))
                              ) : (
                                <p>No shippers available at this time</p>
                              )}

                              {filterPublicShippersData?.length > 0 && (
                                <button
                                  type="button"
                                  className="mt-5 mb-6 sm:mb-6 w-full inline-flex justify-center h-14 rounded-md border border-transparent shadow-sm px-4 py-4 bg-primary text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500  sm:mt-6 sm:text-sm"
                                  onClick={handleSubmit}
                                >
                                  {storing ? <Beat /> : "Continue"}
                                </button>
                              )}
                            </ul>
                            :
                            <>
                              {/* This is for when delivery_mode_checkout_status is unavailable */}
                              <ul>
                                {/* If there are multiple shipper, give room to change selected shippers */}
                                {filterPublicShippersData?.length > 0 ? (
                                  <div>
                                    {filterPublicShippersData?.length > 0 &&
                                      filterPublicShippersData?.map((shipper) => (
                                        <li
                                          className={`border-b py-4 hover:bg-gray-100 group hover:shadow ${selected?.id === shipper?.id
                                            ? "bg-red-50 dark:bg-red-900/20"
                                            : "bg-white dark:bg-[#121212]"
                                            }`}
                                          key={`${shipper.id}`}
                                        >
                                          <div className="ml-3 flex justify-between text-left items-center">
                                            <label
                                              htmlFor={`shipper-radio-${shipper.id}`}
                                              className="cursor-pointer flex-1"
                                            >
                                              <div className="flex items-center">
                                                <SHIPPERS
                                                  className={`mr-3 flex-shrink-0 h-[40px] w-[40px] ${selected?.id === shipper?.id
                                                    ? "text-primary"
                                                    : "text-gray-400 group-hover:text-gray-500"
                                                    }`}
                                                  aria-hidden="true"
                                                />
                                                <div className="ml-3">
                                                  <p
                                                    className={`font-medium text-base ${selected?.id === shipper?.id
                                                      ? "text-primary"
                                                      : ""
                                                      }`}
                                                  >
                                                    {shipper?.name}
                                                  </p>
                                                  {/* <p className="text-sm text-gray-400 font-normal">
                                            {mode.title}
                                          </p> */}
                                                </div>
                                              </div>
                                            </label>

                                            <input
                                              id={`shipper-radio-${shipper.id}`}
                                              name="shipper-radio-group"
                                              type={"radio"}
                                              checked={selected?.id === shipper?.id}
                                              className="w-6 h-6 text-primary bg-white dark:bg-[#121212] border-primary focus:ring-primary cursor-pointer"
                                              onChange={() => setSelected(shipper)}
                                            />
                                          </div>
                                        </li>
                                      ))}
                                  </div>
                                ) : (
                                  <p>No shippers available at this time</p>
                                )}

                                {filterPublicShippersData?.length > 0 && (
                                  <button
                                    type="button"
                                    className="mt-5 mb-6 sm:mb-6 w-full inline-flex justify-center h-14 rounded-md border border-transparent shadow-sm px-4 py-4 bg-primary text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500  sm:mt-6 sm:text-sm"
                                    onClick={handleSubmit}
                                  >
                                    {storing ? <Beat /> : "Continue"}
                                  </button>
                                )}
                              </ul>
                            </>
                          }
                        </>
                      }

                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}

const ShipperDeliveryModes = ({ shipper, selected, setSelected }) => {
  const [dropdown, setDropdown] = useState(false);
  const [selectedDeliveryMode, setSelectedDeliveryMode] = useState(null);

  // Auto-expand if this shipper is selected
  useEffect(() => {
    if (selected?.id === shipper?.id) {
      setDropdown(true);
      // Set the selected delivery mode if it exists
      if (selected?.delivery_mode_id) {
        setSelectedDeliveryMode(selected?.delivery_mode_id);
      }
    }
  }, [selected, shipper?.id]);

  const handleDeliveryModeSelect = (mode) => {
    setSelectedDeliveryMode(mode?.id);
    setSelected({
      ...shipper,
      delivery_mode_id: mode?.id,
      delivery_mode: mode
    });
  };

  return (
    <li
      className={`border-b py-4 hover:bg-gray-100 group hover:shadow ${selected?.id === shipper?.id
        ? "bg-red-50 dark:bg-red-900/20"
        : "bg-white dark:bg-[#121212]"
        }`}
    >
      <div className="w-full">
        <div className="px-3 flex justify-between text-left items-start">
          <div className="cursor-pointer flex-1">
            <div className="flex items-start w-full">
              <SHIPPERS
                className={`mr-3 flex-shrink-0 h-[40px] w-[40px] ${selected?.id === shipper?.id
                  ? "text-primary"
                  : "text-gray-400 group-hover:text-gray-500"
                  }`}
                aria-hidden="true"
              />
              <div className="ml-3 mt-1 w-full">
                <div 
                  className="w-full flex justify-between items-center cursor-pointer"
                  onClick={() => setDropdown(!dropdown)}
                  role="button"
                  aria-label="shipper-dropdown-toggle"
                  aria-expanded={dropdown}
                >
                  <p
                    className={`font-medium capitalize text-base ${selected?.id === shipper?.id ? "text-primary" : ""
                      }`}
                  >
                    {shipper?.name}
                  </p>

                  <ChevronDownIcon
                    className={`h-5 w-5 flex-none transition-transform duration-300 ${dropdown ? "" : "rotate-180"
                      }`}
                  />
                </div>

                {dropdown && (
                  <div className="w-full mt-3">
                    {shipper?.delivery_modes?.length > 0 ? (
                      shipper?.delivery_modes?.map((mode) => (
                        <div
                          key={mode?.id}
                          className="w-full flex justify-between items-center py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 rounded pl-1"
                          onClick={() => handleDeliveryModeSelect(mode)}
                        >
                          <p className="capitalize">
                            <span className="text-base font-bold">
                              {mode?.title}:
                            </span>
                            <span className="text-sm ml-3">
                              Additional fee: NGN{" "}
                              {numberWithCommas(mode?.pivot?.charge)}
                            </span>
                          </p>
                          <input
                            id={`delivery-mode-${mode?.id}`}
                            name={`delivery-mode-group-${shipper.id}`}
                            type={"radio"}
                            checked={
                              selected?.id === shipper?.id &&
                              selectedDeliveryMode === mode?.id
                            }
                            onChange={() => handleDeliveryModeSelect(mode)}
                            className="
                                  appearance-none
                                  w-5 h-5
                                  rounded-full
                                  border-2 border-gray-400
                                  bg-white dark:bg-[#121212]
                                  cursor-pointer
                                  checked:border-[#00D220]
                                  checked:bg-[#00D220]
                                  focus:outline-none
                                  focus:border-[#00D220]
                                  focus:ring-[#00D220]
                                  checked:focus:border-[#00D220]
                                  relative
                                  after:content-['']
                                  after:absolute
                                  after:w-2
                                  after:h-2
                                  after:rounded-full
                                  after:bg-white
                                  after:top-1/2
                                  after:left-1/2
                                  after:-translate-x-1/2
                                  after:-translate-y-1/2
                                  after:opacity-0
                                  checked:after:opacity-100
                                "
                          />
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-gray-500 py-2">
                        No delivery modes available
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>


        </div>
      </div>
    </li>
  );
}